import {
  computed, onScopeDispose, ref, toValue, watch, type MaybeRefOrGetter,
} from 'vue';
import { useRouter } from 'vue-router';
import {
  useAiPendingActionQuery,
  useCancelAiPendingActionMutation,
  useConfirmAiPendingActionMutation,
} from '@/types/vue-apollo-helpers';
import { AiActionTier, AiPendingActionStatus } from '@/types/composition-functions';
import type { AiPendingActionFieldsFragment } from '@/types/operations';
import { extractGraphQLError } from '@/utils/graphqlError';
import { effectivePendingStatus, resolveAiOpenTarget, secondsUntil } from '@/utils/aiActions';

/** While a bulk action is EXECUTING the card re-reads it this often (the run itself polls faster). */
export const PENDING_ACTION_POLL_MS = 5000;

export const CONFIRM_FAILED_MESSAGE = 'We couldn\'t confirm this action. Check its status below before trying again.';
export const CANCEL_FAILED_MESSAGE = 'We couldn\'t cancel this action. Try again.';

/**
 * One proposed action on an `action_confirmation` card: the server's preview,
 * the expiry clock, and the person's Confirm / Cancel.
 *
 * Confirm can never double-fire from this card: one request at a time, and
 * once the server has accepted a confirm this instance never sends another.
 * The server's compare-and-set is the final guard either way.
 */
export function useAiPendingAction(id: MaybeRefOrGetter<string | null | undefined>) {
  const actionId = computed(() => toValue(id) ?? '');

  // Polls only while a bulk action is EXECUTING (set by the watch below).
  const pollInterval = ref(0);

  const { result, loading, error, refetch } = useAiPendingActionQuery(
    () => ({ id: actionId.value }),
    () => ({ enabled: !!actionId.value, fetchPolicy: 'cache-and-network', pollInterval: pollInterval.value }),
  );

  const { mutate: confirmMutate } = useConfirmAiPendingActionMutation({});
  const { mutate: cancelMutate } = useCancelAiPendingActionMutation({});

  // The mutation's own answer, kept in case the cached query still says
  // PENDING (normalisation lag): the card must show the outcome, not the
  // button again.
  const latest = ref<AiPendingActionFieldsFragment | null>(null);

  const pendingAction = computed<AiPendingActionFieldsFragment | null>(() => {
    const fromQuery = result.value?.aiPendingAction ?? null;
    if (!latest.value) return fromQuery;
    if (!fromQuery || fromQuery.status === AiPendingActionStatus.Pending) return latest.value;
    return fromQuery;
  });

  const now = ref(Date.now());
  let clock: ReturnType<typeof setInterval> | null = null;
  const stopClock = () => {
    if (clock) clearInterval(clock);
    clock = null;
  };

  const rawStatus = computed(() => pendingAction.value?.status ?? null);

  // Tick once a second only while the action can still be confirmed.
  watch(rawStatus, (status) => {
    if (status === AiPendingActionStatus.Pending) {
      now.value = Date.now();
      if (!clock) clock = setInterval(() => { now.value = Date.now(); }, 1000);
    } else {
      stopClock();
    }
  }, { immediate: true });

  const status = computed<AiPendingActionStatus | null>(() =>
    (pendingAction.value
      ? effectivePendingStatus(pendingAction.value.status, pendingAction.value.expiresAt, now.value)
      : null));

  const secondsLeft = computed(() =>
    (status.value === AiPendingActionStatus.Pending ? secondsUntil(pendingAction.value?.expiresAt, now.value) : null));

  const isExpired = computed(() => status.value === AiPendingActionStatus.Expired);
  const isBulk = computed(() => pendingAction.value?.tier === AiActionTier.Bulk);
  const preview = computed(() => pendingAction.value?.preview ?? null);
  const run = computed(() => pendingAction.value?.run ?? null);
  const changes = computed(() => preview.value?.changes ?? []);

  // "Open in form builder" once DONE: the server names the route, the router
  // decides whether it exists here (unknown routes give no button).
  const router = useRouter();
  const resultTarget = computed(() => {
    const link = pendingAction.value?.resultLink;
    if (!link || status.value !== AiPendingActionStatus.Done || !router) return null;
    const target = resolveAiOpenTarget(router, link.routeName, link.params);
    return target ? { ...target, label: link.label } : null;
  });
  const openResult = () => {
    if (!resultTarget.value) return;
    router.push(resultTarget.value.location).catch((e: unknown) => console.error(e));
  };

  watch(rawStatus, (s) => {
    pollInterval.value = s === AiPendingActionStatus.Executing ? PENDING_ACTION_POLL_MS : 0;
  }, { immediate: true });

  const submitting = ref<'confirm' | 'cancel' | null>(null);
  const confirmAccepted = ref(false);
  const actionError = ref('');

  const canConfirm = computed(() =>
    status.value === AiPendingActionStatus.Pending && !submitting.value && !confirmAccepted.value);
  const canCancel = computed(() =>
    status.value === AiPendingActionStatus.Pending && !submitting.value && !confirmAccepted.value);

  const notFound = computed(() => !!actionId.value && !loading.value && !error.value && !pendingAction.value);

  /** Re-read the action from the server (after a refusal, or when its run settles). */
  const refresh = () => {
    Promise.resolve(refetch())?.catch((e: unknown) => console.error(e));
  };

  const confirm = async(): Promise<boolean> => {
    if (!canConfirm.value) return false;
    submitting.value = 'confirm';
    actionError.value = '';
    try {
      const res = await confirmMutate({ id: actionId.value });
      const updated = res?.data?.confirmAiPendingAction ?? null;
      if (!updated) {
        actionError.value = CONFIRM_FAILED_MESSAGE;
        refresh();
        return false;
      }
      confirmAccepted.value = true;
      latest.value = updated;
      return true;
    } catch (e) {
      actionError.value = extractGraphQLError(e, CONFIRM_FAILED_MESSAGE);
      refresh();
      return false;
    } finally {
      submitting.value = null;
    }
  };

  const cancel = async(): Promise<boolean> => {
    if (!canCancel.value) return false;
    submitting.value = 'cancel';
    actionError.value = '';
    try {
      const res = await cancelMutate({ id: actionId.value });
      const updated = res?.data?.cancelAiPendingAction ?? null;
      if (updated) latest.value = updated;
      return !!updated;
    } catch (e) {
      actionError.value = extractGraphQLError(e, CANCEL_FAILED_MESSAGE);
      refresh();
      return false;
    } finally {
      submitting.value = null;
    }
  };

  onScopeDispose(stopClock);

  return {
    pendingAction,
    preview,
    run,
    changes,
    resultTarget,
    openResult,
    status,
    isBulk,
    isExpired,
    secondsLeft,
    canConfirm,
    canCancel,
    submitting,
    actionError,
    loading,
    error,
    notFound,
    confirm,
    cancel,
    refresh,
    refetch,
  };
}
